import type { JSX } from 'react';
import { FormattedMessage } from 'react-intl';
import numbro from 'numbro';
import type {
  FacilityFormValues,
  FacilityDataKey,
  WizardFormValues,
} from '../../../domain/customerInquiry';
import { coerceToString } from '../../../utils/coerce';
import s from './YourDataInfo3.module.scss';

type FacilityDataKeyOrEmpty = FacilityDataKey | '';

function renderDirektvermarktung(values: WizardFormValues): JSX.Element {
  // TODO: refactor! use only I18N messages!
  const { facilityType } = values;
  let facilityTypeData: FacilityDataKeyOrEmpty = '';
  let facilityName: string | undefined;
  let msgPostfix: 'generated' | 'output' | undefined;

  // which facility
  switch (facilityType) {
    case 'biomasse':
      facilityTypeData = 'bioMasseFacilityData';
      facilityName = 'Biomasseanlagen';
      msgPostfix = 'generated';
      break;
    case 'photovoltaik':
      facilityTypeData = 'solarFacilityData';
      facilityName = 'Photovoltaikanlagen';
      msgPostfix = 'generated';
      break;
    case 'wasser':
      facilityTypeData = 'waterFacilityData';
      facilityName = 'Wasserkraftanlagen';
      msgPostfix = 'output';
      break;
    case 'wind':
      facilityTypeData = 'windFacilityData';
      facilityName = 'Windkraftanlagen';
      msgPostfix = 'output';
      break;
    default:
      break;
  }

  const facilities =
    facilityTypeData !== '' ? (values[facilityTypeData] ?? []) : [];

  const numFacilities = facilities.length;

  let sumGenerated = 0;
  let sumPower = 0;
  facilities.forEach((value: FacilityFormValues) => {
    const generated = numbro.unformat(coerceToString(value.generated));
    if (typeof generated === 'number' && !Number.isNaN(generated)) {
      sumGenerated += generated;
    }

    const power = numbro.unformat(coerceToString(value.output));
    if (typeof power === 'number' && !Number.isNaN(power)) {
      sumPower += power;
    }
  });

  let noSum = false;
  switch (msgPostfix) {
    case 'output':
      if (sumPower === 0) {
        noSum = true;
      }
      break;
    case 'generated':
      if (sumGenerated === 0) {
        noSum = true;
      }
      break;
    default:
      // oxlint-disable-next-line no-console
      console.error(`YourDataInfo3: unknown msgPostfix ${msgPostfix}`);
      break;
  }

  if (noSum) {
    return (
      <FormattedMessage
        id="offerWizard.yourData.info3.direktvermarktung.noSum"
        values={{
          facilityType: (
            <FormattedMessage
              id={`offerWizard.yourData.info3.direktvermarktung.facilityType.${facilityType}`}
            />
          ),
        }}
      />
    );
  }

  return (
    <FormattedMessage
      id={`offerWizard.yourData.info3.direktvermarktung.${msgPostfix}`}
      values={{
        numFacilities,
        facilityName,
        sumGenerated: numbro(sumGenerated).format(),
        sumPower: numbro(sumPower).format(),
      }}
    />
  );
}

function renderPowerDelivery(values: WizardFormValues): JSX.Element {
  const { powerType } = values;
  let consumption: string | undefined;
  switch (powerType) {
    case 'gruenstrom': {
      consumption = values.greenPowerData?.consumption ?? undefined;
      break;
    }
    case 'graustrom': {
      consumption = values.greyPowerData?.consumption ?? undefined;
      break;
    }
    default:
      break;
  }

  const powerTypeMessage = (
    <FormattedMessage
      id={`offerWizard.yourData.info3.powerDelivery.${powerType}`}
    />
  );

  let message;

  if (consumption) {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.powerDelivery"
        values={{
          consumption,
          powerType: powerTypeMessage,
        }}
      />
    );
  } else {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.powerDelivery.noData"
        values={{
          powerType: powerTypeMessage,
        }}
      />
    );
  }

  return message;
}

function renderZertifikate(values: WizardFormValues): JSX.Element {
  const { certificate } = values;
  let consumption: string | null | undefined;
  switch (certificate) {
    case 'co2': {
      consumption = values.co2CertificateData?.consumption;
      break;
    }
    case 'proofOfOrigin': {
      consumption = values.proofOfOriginCertificateData?.consumption;
      break;
    }
    default:
      break;
  }

  const unitMessage = (
    <FormattedMessage
      id={`offerWizard.yourData.info3.certificate.unit.${certificate}`}
    />
  );

  const typeMessage = (
    <FormattedMessage
      id={`offerWizard.yourData.info3.certificate.${certificate}`}
    />
  );

  let message;

  if (consumption) {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.certificate"
        values={{
          unit: unitMessage,
          certificates: typeMessage,
          consumption,
        }}
      />
    );
  } else {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.certificate.noData"
        values={{
          certificates: typeMessage,
        }}
      />
    );
  }
  return message;
}

function renderLargeSolarFacilities(values: WizardFormValues): JSX.Element {
  const { largeSolarFacilityType, largeSolarFacilityData } = values;
  const { sizeOfArea, consumption } = largeSolarFacilityData ?? {};

  const locationMsg = (
    <FormattedMessage
      id={`offerWizard.yourData.info3.pvGrossanlage.${largeSolarFacilityType}`}
    />
  );

  let message;

  if (sizeOfArea && consumption) {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.pvGrossanlage.allData"
        values={{
          consumption,
          location: locationMsg,
          area: sizeOfArea,
        }}
      />
    );
  } else if (sizeOfArea) {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.pvGrossanlage.onlyArea"
        values={{
          location: locationMsg,
          area: sizeOfArea,
        }}
      />
    );
  } else if (consumption) {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.pvGrossanlage.onlyConsumption"
        values={{
          consumption,
          location: locationMsg,
        }}
      />
    );
  } else {
    message = (
      <FormattedMessage
        id="offerWizard.yourData.info3.pvGrossanlage.noData"
        values={{
          location: locationMsg,
        }}
      />
    );
  }

  return message;
}

function renderBusinessCharging(values: WizardFormValues): JSX.Element {
  const { numberOfParkingSpaces, hasOwnCarFleet, numberOfElectricCars } =
    values.businessChargingData ?? {};

  const hasNumberOfParkingSpacesBeenEntered =
    numberOfParkingSpaces !== undefined && numberOfParkingSpaces !== '';

  const hasHasOwnCarFleetBeenEntered = hasOwnCarFleet !== undefined;

  const hasNumberOfElectricCarsBeenEntered =
    numberOfElectricCars !== undefined && numberOfElectricCars !== '';

  if (
    !hasNumberOfParkingSpacesBeenEntered &&
    !hasHasOwnCarFleetBeenEntered &&
    !hasNumberOfElectricCarsBeenEntered
  ) {
    return (
      <FormattedMessage
        id="offerWizard.yourData.info3.businessCharging.noDataEntered"
        values={{ numberOfParkingSpaces }}
      />
    );
  }

  return (
    <ul className={s.list}>
      {hasNumberOfParkingSpacesBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id="offerWizard.yourData.info3.businessCharging.parkingSpaces"
            values={{ numberOfParkingSpaces }}
          />
        </li>
      )}
      {hasHasOwnCarFleetBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id={
              hasOwnCarFleet === 'true'
                ? 'offerWizard.yourData.info3.businessCharging.ownFleet.yes'
                : 'offerWizard.yourData.info3.businessCharging.ownFleet.no'
            }
          />
        </li>
      )}
      {hasNumberOfElectricCarsBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id="offerWizard.yourData.info3.businessCharging.electricCars"
            values={{ numberOfElectricCars }}
          />
        </li>
      )}
    </ul>
  );
}

function renderImmoCharging(values: WizardFormValues): JSX.Element {
  const {
    numberOfUnits,
    numberOfGarageObjects,
    numberOfParkingSpaces,
    isResidentialOrCommercial,
  } = values.immoChargingData ?? {};

  const hasNumberOfResidentialUnitsBeenEntered =
    numberOfUnits !== undefined && numberOfUnits !== '';

  const hasNumberOfGarageObjectsBeenEntered =
    numberOfGarageObjects !== undefined && numberOfGarageObjects !== '';

  const hasNumberOfParkingSpacesBeenEntered =
    numberOfParkingSpaces !== undefined && numberOfParkingSpaces !== '';

  return (
    <ul className={s.list}>
      {hasNumberOfResidentialUnitsBeenEntered &&
        isResidentialOrCommercial === 'residential' && (
          <li className={s.listItem}>
            <FormattedMessage
              id="offerWizard.yourData.info3.immoCharging.numberOfResidentialUnits"
              values={{ numberOfUnits }}
            />
          </li>
        )}
      {hasNumberOfResidentialUnitsBeenEntered &&
        isResidentialOrCommercial === 'commercial' && (
          <li className={s.listItem}>
            <FormattedMessage
              id="offerWizard.yourData.info3.immoCharging.numberOfOfficeUnits"
              values={{ numberOfUnits }}
            />
          </li>
        )}
      {hasNumberOfGarageObjectsBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id="offerWizard.yourData.info3.immoCharging.numberOfGarageObjects"
            values={{ numberOfGarageObjects }}
          />
        </li>
      )}
      {hasNumberOfParkingSpacesBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id="offerWizard.yourData.info3.immoCharging.numberOfParkingSpaces"
            values={{ numberOfParkingSpaces }}
          />
        </li>
      )}
    </ul>
  );
}

function renderCoopCharging(values: WizardFormValues): JSX.Element {
  const { numberOfHotelLocations = '', numberOfRooms = '' } =
    values.coopChargingData ?? {};
  const hasNumberOfHotelLocationsBeenEntered = numberOfHotelLocations !== '';
  const hasNumberOfRoomsBeenEntered = numberOfRooms !== '';

  return (
    <ul className={s.list}>
      {hasNumberOfHotelLocationsBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id="offerWizard.yourData.info3.coopCharging.numberOfHotelLocations"
            values={{ numberOfHotelLocations }}
          />
        </li>
      )}
      {hasNumberOfRoomsBeenEntered && (
        <li className={s.listItem}>
          <FormattedMessage
            id="offerWizard.yourData.info3.coopCharging.numberOfRooms"
            values={{ numberOfRooms }}
          />
        </li>
      )}
    </ul>
  );
}

interface YourDataInfo3Props {
  values: WizardFormValues;
}

function YourDataInfo3({ values }: YourDataInfo3Props) {
  const { product } = values;

  let content: JSX.Element;
  switch (product) {
    case 'direktvermarktung': {
      content = renderDirektvermarktung(values);
      break;
    }
    case 'zertifikate': {
      content = renderZertifikate(values);
      break;
    }
    case 'pvGrossanlage': {
      content = renderLargeSolarFacilities(values);
      break;
    }
    case 'stromlieferung': {
      content = renderPowerDelivery(values);
      break;
    }
    case 'businessCharging': {
      content = renderBusinessCharging(values);
      break;
    }
    case 'immoCharging': {
      content = renderImmoCharging(values);
      break;
    }
    case 'coopCharging': {
      content = renderCoopCharging(values);
      break;
    }
    default:
      content = <>TODO</>;
  }
  return content;
}

export default YourDataInfo3;
